import clsx from 'clsx';
import moment from 'moment';
import { LastOrder } from 'domains/cart';
import { GET_ENUMS, GET_PARAMS } from 'const';
import { OrderStatus, OrderType } from 'models';
import { useNavigate } from 'react-router-dom';
import { usePrepareLink } from 'hooks/router';
import { useTranslation } from 'react-i18next';
import { FEDateUtils } from 'utils';
import { FEButton } from 'components';
import { getOrderStatuses } from 'models/OrderStatuses';
import style from './style.module.css';
import OrderProgress from 'ui-kit/components/OrderProgress/OrderProgress';

const formatTimeInterval = (timeInterval: moment.Moment, timeError: number) => {
  if (timeError === 0) {
    return timeInterval.format('HH:mm');
  }

  const first = timeInterval.clone().subtract(timeError, 'minutes').format('HH:mm');
  const second = timeInterval.clone().add(timeError, 'minutes').format('HH:mm');

  return `${first} до ${second}`;
};

const getTime = (
  dateTime: string,
  timeError: number,
  toUTC?: boolean,
) => {
  const momentedDateTime = moment(dateTime);

  if (toUTC) {
    FEDateUtils.addUtcOffset(momentedDateTime);
  }

  return formatTimeInterval(momentedDateTime, timeError);
};

const LastOrderBanner = ({ lastOrder }: { lastOrder: LastOrder | null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const orderLink = usePrepareLink({
    to: '/profile',
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.orders,
      ...(lastOrder?.order.id
        ? { [GET_PARAMS.orderId]: lastOrder.order.id }
        : {}),
    },
  });

  

  if (lastOrder === null) {
    return null;
  }

  const {
    order: {
      type,
      number,
      status,
      receiptInformation,
    },
  } = lastOrder;

  const {
    deliveryTimeErrorInMinutes,
    dateTimeTo,
  } = receiptInformation;

  const statuses = getOrderStatuses(t);

  const getStatus = (
    orderType: OrderType,
    orderStatus: OrderStatus,
    orderDateTimeTo: string,
  ) => {
    if (
      orderStatus !== 'CANCELLED' &&
      orderStatus !== 'DONE' &&
      moment(orderDateTimeTo).isAfter(moment(), 'day')
    ) {
      return t('orders.statuses.PREORDER');
    }

    return statuses[orderType][orderStatus];
  };

  const getAddress = () => {
    if (type === 'COURIER') {
      return receiptInformation.receivingAddress.address;
    }

    return receiptInformation.receivingRestaurant.name;
  };

  const handleOpenOrder = () => {
    navigate(orderLink);
  };

  return (
    <section className={style.root} onClick={handleOpenOrder}>
      <div className={style.header}>
        <p className={clsx('caption-1', style.address)}>
          {getAddress()}
        </p>

        <p className={clsx('caption-1', style.orderNumber)}>
          {`№ ${number}`}
        </p>
      </div>

      <div className={style.progress}>
        <OrderProgress type={type} status={status} />
      </div>

      <div className={style.content}>
        <p className={clsx('title', style.status)}>
          {getStatus(type, status, dateTimeTo)}
        </p>

        <p className={clsx('text-1', style.time)}>
          {`${
            type === 'COURIER'
              ? t('orders.statusesLabels.courier.rest')
              : t('orders.statusesLabels.goToPlace.rest')
          } ${getTime(
            dateTimeTo,
            deliveryTimeErrorInMinutes,
            true,
          )}`}
        </p>
      </div>

      <FEButton
        className={style.button}
        onClick={handleOpenOrder}
      >
        Подробнее
      </FEButton>
    </section>
  );
};

export default LastOrderBanner;